import { useState, useCallback, useMemo } from 'react'
import { Toast } from 'react-vant'
import { ArrowLeft } from '@react-vant/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import request from '../../api/request'

// 12个年级选项
const GRADES = [
  '一年级', '二年级', '三年级', '四年级', '五年级', '六年级',
  '初一', '初二', '初三', '高一', '高二', '高三'
]

// 项目配置信息 (与 App 端完全对齐)
const ITEM_CONFIG = {
  height: { label: '身高', unit: 'cm', placeholder: '如 170' },
  weight: { label: '体重', unit: 'kg', placeholder: '如 60' },
  vital_capacity: { label: '肺活量', unit: 'ml', placeholder: '如 3500' },
  run_50m: { label: '50米跑', unit: 's', placeholder: '如 7.5' },
  sit_and_reach: { label: '坐位体前屈', unit: 'cm', placeholder: '如 15.0' },
  skip_rope: { label: '一分钟跳绳', unit: '次', placeholder: '如 140' },
  sit_up: { label: '一分钟仰卧起坐', unit: '次', placeholder: '如 45' },
  standing_long_jump: { label: '立定跳远', unit: 'cm', placeholder: '如 220' },
  pull_up: { label: '引体向上', unit: '次', placeholder: '如 10' },
  run_800m: { label: '800米跑', unit: '分.秒', placeholder: '如 3.30' },
  run_1000m: { label: '1000米跑', unit: '分.秒', placeholder: '如 3.50' },
  run_50m_8x: { label: '50m×8往返跑', unit: '分.秒', placeholder: '如 1.36' },
}

export default function PhysicalTestCalculator() {
  const navigate = useNavigate()
  const location = useLocation()

  // 判断是否为学生端路由
  const isStudent = location.pathname.startsWith('/student')

  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [activeField, setActiveField] = useState(null)

  const [params, setParams] = useState({
    grade: '一年级',
    gender: 'male',
    test_items: {},
  })

  // 获取当前年级与性别下的必测项目清单
  const getRequiredItems = useCallback((grade, gender) => {
    const baseItems = ['height', 'weight', 'vital_capacity', 'run_50m', 'sit_and_reach']

    if (['一年级', '二年级'].includes(grade)) {
      return [...baseItems, 'skip_rope']
    }
    if (['三年级', '四年级'].includes(grade)) {
      return [...baseItems, 'skip_rope', 'sit_up']
    }
    if (['五年级', '六年级'].includes(grade)) {
      return [...baseItems, 'skip_rope', 'sit_up', 'run_50m_8x']
    }

    // 初中与高中
    const common = [...baseItems, 'standing_long_jump']
    if (gender === 'male' || gender === '男') {
      return [...common, 'pull_up', 'run_1000m']
    } else {
      return [...common, 'sit_up', 'run_800m']
    }
  }, [])

  const currentItems = useMemo(() => {
    return getRequiredItems(params.grade, params.gender)
  }, [params.grade, params.gender, getRequiredItems])

  // 切换年级
  const handleGradeChange = (g) => {
    setParams(prev => ({ ...prev, grade: g }))
    setResult(null)
  }

  // 切换性别
  const handleGenderChange = (gender) => {
    setParams(prev => ({ ...prev, gender }))
    setResult(null)
  }

  // 修改单个项目数值
  const handleItemChange = (code, val) => {
    // 允许输入数字、小数点与负号（针对坐位体前屈）
    const cleaned = val !== undefined && val !== null ? String(val).replace(/[^0-9.-]/g, '') : ''
    setParams(prev => ({
      ...prev,
      test_items: {
        ...prev.test_items,
        [code]: cleaned,
      }
    }))
  }

  // 清空所有项目数值与计算结果
  const handleClear = () => {
    setParams(prev => ({
      ...prev,
      test_items: {},
    }))
    setResult(null)
    Toast.info('已清空所有输入数值')
  }

  // 获取计算出的精确 BMI 数值
  const getBmiValue = useCallback(() => {
    const bmiItem = result?.item_scores?.bmi
    if (bmiItem?.value !== undefined && bmiItem?.value !== null && !isNaN(Number(bmiItem.value))) {
      return Number(bmiItem.value).toFixed(1)
    }
    const h = parseFloat(params.test_items?.height)
    const w = parseFloat(params.test_items?.weight)
    if (h > 0 && w > 0) {
      const hMeter = h / 100
      return (w / (hMeter * hMeter)).toFixed(1)
    }
    return null
  }, [result, params.test_items])

  const bmiVal = getBmiValue()

  // 等级色彩与文字
  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A':
      case '优秀': return '#059669'
      case 'B':
      case '良好': return '#0891b2'
      case 'C':
      case '及格': return '#d97706'
      case 'D':
      case '不及格': return '#e11d48'
      default: return '#64748b'
    }
  }

  const getGradeBg = (grade) => {
    switch (grade) {
      case 'A':
      case '优秀': return '#ECFDF5'
      case 'B':
      case '良好': return '#ECFEFF'
      case 'C':
      case '及格': return '#FFFBEB'
      case 'D':
      case '不及格': return '#FFF1F2'
      default: return '#f1f5f9'
    }
  }

  const getGradeText = (grade) => {
    switch (grade) {
      case 'A': return '优秀'
      case 'B': return '良好'
      case 'C': return '及格'
      case 'D': return '不及格'
      default: return grade || '未知'
    }
  }

  // 提交成绩测算
  const handleCalculate = async () => {
    // 验证当前适用必填项目
    for (const item of currentItems) {
      const val = params.test_items[item]
      if (val === undefined || val === null || String(val).trim() === '') {
        Toast.info(`请输入${ITEM_CONFIG[item]?.label || item}数值`)
        setActiveField(item)
        return
      }
    }

    setLoading(true)
    try {
      const numericItems = {}
      for (const [k, v] of Object.entries(params.test_items)) {
        if (v !== '' && v !== null && v !== undefined) {
          numericItems[k] = parseFloat(v) || 0
        }
      }

      const payload = {
        grade: params.grade,
        gender: params.gender,
        test_items: numericItems,
      }

      // 请求计算接口 (支持 /sports/standards/calculate-total/ 与备用 fallback)
      let resData = null
      try {
        const response = await request.post('/sports/standards/calculate-total/', payload)
        if (response && response.success) {
          resData = response.data
        } else if (response && response.total_score !== undefined) {
          resData = response
        }
      } catch (err) {
        console.warn('First calculate endpoint failed, trying backup endpoint...', err)
        const fallbackRes = await request.post('/sports/tests/calculate-score/', {
          ...payload,
          gender: params.gender === 'male' ? '男' : '女',
        })
        if (fallbackRes) {
          resData = {
            total_score: fallbackRes.composite_score !== undefined ? fallbackRes.composite_score : fallbackRes.total_score,
            standard_score: fallbackRes.standard_score !== undefined ? fallbackRes.standard_score : fallbackRes.total_score,
            bonus_score: fallbackRes.bonus_score || 0,
            grade: fallbackRes.grade_level || fallbackRes.grade_name || 'C',
            item_scores: fallbackRes.item_scores || {},
          }
        }
      }

      if (resData) {
        setResult(resData)
        Toast.success('成绩计算完成')
        setTimeout(() => {
          const resultElement = document.getElementById('calc-result-card')
          if (resultElement) {
            resultElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 120)
      } else {
        Toast.fail('计算未返回有效结果')
      }
    } catch (error) {
      console.error('成绩计算异常:', error)
      Toast.fail(error.response?.data?.detail || '计算失败，请检查输入')
    } finally {
      setLoading(false)
    }
  }

  // 跳转到标准表
  const handleGoStandards = () => {
    if (isStudent) {
      navigate('/student/physical-test/standards')
    } else {
      navigate('/physical-test/standards')
    }
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] text-[#0f172a] pb-24 font-sans select-none">
      {/* 顶部星瀚空天大页眉 (与 App 端视觉完全对齐) */}
      <div
        className="sticky top-0 z-30 px-4 pt-3 pb-4 shadow-sm"
        style={{
          background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
        }}
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>

          <div className="text-center">
            <h1 className="text-[18px] font-bold text-white tracking-wide leading-tight">成绩计算</h1>
            <p className="text-[11.5px] text-white/80 font-normal mt-0.5">国标模拟测评 · 综合总分换算</p>
          </div>

          <button
            onClick={handleGoStandards}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white/20 active:bg-white/30 text-white text-[12px] font-medium transition-colors"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
            </svg>
            <span>标准表</span>
          </button>
        </div>
      </div>

      <div className="px-3.5 pt-3 space-y-3.5 max-w-lg mx-auto">
        {/* 卡片 1: 测评基准信息 */}
        <div className="bg-white rounded-2xl p-4 shadow-[0_4px_16px_rgba(15,23,42,0.05)] border border-[#e2e8f0]">
          <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-[#f1f5f9]">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#e8f6f0] flex items-center justify-center text-[#056b4f]">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <span className="text-[15px] font-bold text-[#0f172a]">测评基准信息</span>
            </div>
          </div>

          {/* 年级选择 */}
          <div className="mb-3.5">
            <div className="text-[12.5px] font-semibold text-[#475569] mb-2 flex items-center justify-between">
              <span>选择年级</span>
              <span className="text-[11px] text-[#056b4f] font-normal">当前：{params.grade}</span>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {GRADES.map(g => {
                const isActive = params.grade === g
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => handleGradeChange(g)}
                    className={`py-2 px-1 text-center rounded-lg text-[12.5px] transition-all font-medium ${
                      isActive
                        ? 'bg-[#056b4f] text-white shadow-sm font-bold scale-[1.02]'
                        : 'bg-[#f1f5f9] text-[#475569] active:bg-[#e2e8f0]'
                    }`}
                  >
                    {g}
                  </button>
                )
              })}
            </div>
          </div>

          {/* 性别选择 */}
          <div>
            <div className="text-[12.5px] font-semibold text-[#475569] mb-2">选择性别</div>
            <div className="grid grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={() => handleGenderChange('male')}
                className={`py-2.5 px-3 rounded-xl border flex items-center justify-center gap-2 font-bold text-[14px] transition-all ${
                  params.gender === 'male'
                    ? 'bg-[#056b4f] text-white border-[#056b4f] shadow-sm'
                    : 'bg-[#f0fdf4] text-[#056b4f] border-[#a7f3d0] active:bg-[#e8f6f0]'
                }`}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="10" cy="14" r="5"></circle>
                  <line x1="19" y1="5" x2="13.5" y2="10.5"></line>
                  <polyline points="15 5 19 5 19 9"></polyline>
                </svg>
                <span>男生</span>
              </button>

              <button
                type="button"
                onClick={() => handleGenderChange('female')}
                className={`py-2.5 px-3 rounded-xl border flex items-center justify-center gap-2 font-bold text-[14px] transition-all ${
                  params.gender === 'female'
                    ? 'bg-[#f43f5e] text-white border-[#f43f5e] shadow-sm'
                    : 'bg-[#fff1f2] text-[#e11d48] border-[#fecdd3] active:bg-[#ffe4e6]'
                }`}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="10" r="5"></circle>
                  <line x1="12" y1="15" x2="12" y2="21"></line>
                  <line x1="9" y1="18" x2="15" y2="18"></line>
                </svg>
                <span>女生</span>
              </button>
            </div>
          </div>
        </div>

        {/* 卡片 2: 测试数据输入 */}
        <div className="bg-white rounded-2xl p-4 shadow-[0_4px_16px_rgba(15,23,42,0.05)] border border-[#e2e8f0]">
          <div className="flex items-center justify-between pb-2.5 mb-3 border-b border-[#f1f5f9]">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#e8f6f0] flex items-center justify-center text-[#056b4f]">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 20V10"></path>
                  <path d="M12 20V4"></path>
                  <path d="M6 20v-6"></path>
                </svg>
              </div>
              <span className="text-[15px] font-bold text-[#0f172a]">项目原始测试数值</span>
            </div>

            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#f0fdf4] border border-[#a7f3d0] text-[#056b4f] text-[11.5px] font-semibold active:bg-[#e8f6f0] transition-colors"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              <span>清空数值</span>
            </button>
          </div>

          {/* 输入表单网格 */}
          <div className="grid grid-cols-2 gap-2.5">
            {currentItems.map(code => {
              const val = params.test_items[code] || ''
              const config = ITEM_CONFIG[code] || { label: code, unit: '', placeholder: '' }
              const isFocused = activeField === code

              return (
                <div
                  key={code}
                  className={`p-2.5 rounded-xl border transition-all ${
                    isFocused
                      ? 'border-[#056b4f] bg-white ring-2 ring-[#056b4f]/20 shadow-sm'
                      : 'border-[#e2e8f0] bg-[#f8fafc]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[12px] font-semibold text-[#475569]">{config.label}</span>
                    <span className="text-[11px] text-[#94a3b8] font-medium">{config.unit}</span>
                  </div>

                  <div className="relative flex items-center">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={val}
                      placeholder={config.placeholder}
                      onFocus={() => setActiveField(code)}
                      onBlur={() => setActiveField(null)}
                      onChange={(e) => handleItemChange(code, e.target.value)}
                      className="w-full bg-transparent text-[16px] font-bold text-[#0f172a] placeholder-[#94a3b8] focus:outline-none"
                    />
                    {val !== '' && (
                      <button
                        type="button"
                        onClick={() => handleItemChange(code, '')}
                        className="w-4 h-4 rounded-full bg-[#cbd5e1] text-white flex items-center justify-center text-[11px] ml-1 active:bg-[#94a3b8]"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* 卡片 3: 计算结果展示 (当计算完成后呈现) */}
        {result && (
          <div
            id="calc-result-card"
            className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(15,23,42,0.06)] border border-[#e2e8f0] animate-fadeIn"
          >
            {/* 顶栏总分概览 */}
            <div className="p-4 bg-[#f8fafc] border-b border-[#e2e8f0] flex items-center justify-between">
              {/* 左侧：综合总分与等级徽标 */}
              <div className="flex-1 flex flex-col items-center justify-center border-r border-[#e2e8f0] pr-4">
                <span className="text-[12px] font-medium text-[#64748b]">综合总分</span>
                <span
                  className="text-[40px] font-extrabold leading-tight my-0.5 tracking-tight"
                  style={{ color: getGradeColor(result.grade) }}
                >
                  {result.total_score}
                </span>
                <span
                  className="px-3 py-0.5 rounded-full text-[12px] font-bold"
                  style={{
                    backgroundColor: getGradeBg(result.grade),
                    color: getGradeColor(result.grade),
                  }}
                >
                  {getGradeText(result.grade)}
                </span>
              </div>

              {/* 右侧：标准分、附加分与 BMI */}
              <div className="flex-1 pl-4 space-y-2">
                <div className="flex items-center justify-between text-[12.5px]">
                  <span className="text-[#64748b]">标准分</span>
                  <span className="font-bold text-[#0f172a]">{result.standard_score}</span>
                </div>
                <div className="flex items-center justify-between text-[12.5px]">
                  <span className="text-[#64748b]">附加分</span>
                  <span className={`font-bold ${result.bonus_score > 0 ? 'text-[#00b578]' : 'text-[#0f172a]'}`}>
                    {result.bonus_score > 0 ? `+${result.bonus_score}` : result.bonus_score}
                  </span>
                </div>
                {bmiVal && (
                  <div className="flex items-center justify-between text-[12.5px]">
                    <span className="text-[#64748b]">BMI 指数</span>
                    <span className="font-bold text-[#056b4f]">{bmiVal}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 单项评分清单表格 */}
            <div className="p-3.5">
              <div className="flex items-center justify-between pb-2 mb-1 border-b border-[#f1f5f9] text-[12px] font-semibold text-[#64748b]">
                <span className="flex-[2] text-left">测试项目</span>
                <span className="flex-1 text-center">得分</span>
                <span className="flex-1 text-right">评定等级</span>
              </div>

              <div className="divide-y divide-[#f1f5f9]">
                {Object.entries(result.item_scores || {}).map(([code, itemData]) => {
                  const isBmi = code === 'bmi'
                  const itemConfig = ITEM_CONFIG[code] || {}
                  const scoreVal = typeof itemData === 'object' && itemData !== null ? itemData.score : itemData
                  const itemGrade = typeof itemData === 'object' && itemData !== null ? itemData.grade : '-'
                  const bonusVal = typeof itemData === 'object' && itemData !== null ? itemData.bonus : 0

                  return (
                    <div key={code} className="py-2.5 flex items-center justify-between">
                      <div className="flex-[2] text-left">
                        <div className="text-[13.5px] font-bold text-[#0f172a]">
                          {isBmi ? 'BMI 身体形态' : (itemConfig.label || code)}
                        </div>
                        {isBmi && bmiVal && (
                          <div className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-[#f0fdf4] border border-[#a7f3d0] text-[10.5px] text-[#056b4f] font-semibold">
                            计算值: {bmiVal} kg/m²
                          </div>
                        )}
                      </div>

                      <div className="flex-1 text-center font-bold text-[14px] text-[#0f172a]">
                        <span>{scoreVal !== undefined && scoreVal !== null ? scoreVal : '-'}</span>
                        {bonusVal > 0 && (
                          <span className="text-[11.5px] text-[#00b578] ml-1">+{bonusVal}</span>
                        )}
                      </div>

                      <div className="flex-1 text-right">
                        <span
                          className="inline-block px-2 py-0.5 rounded text-[11px] font-bold min-w-[42px] text-center"
                          style={{
                            backgroundColor: getGradeBg(itemGrade),
                            color: getGradeColor(itemGrade),
                          }}
                        >
                          {getGradeText(itemGrade)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="mt-3 pt-2 text-center border-t border-[#f1f5f9] text-[11px] text-[#94a3b8]">
                依据：国家学生体质健康标准（2014年修订）
              </div>
            </div>
          </div>
        )}

        {/* 底部操作按钮组合：清空 + 开始计算 */}
        <div className="flex items-center gap-2.5 pt-2">
          <button
            type="button"
            onClick={handleClear}
            className="h-12 px-5 rounded-full border border-[#cbd5e1] bg-white text-[#475569] font-bold text-[14.5px] flex items-center justify-center gap-1.5 active:bg-[#f1f5f9] transition-all shadow-sm"
          >
            <svg className="w-4 h-4 text-[#64748b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            <span>清空</span>
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={handleCalculate}
            className={`flex-1 h-12 rounded-full font-bold text-[15.5px] text-white flex items-center justify-center gap-2 transition-all shadow-md ${
              loading
                ? 'bg-[#94a3b8] cursor-not-allowed'
                : 'bg-gradient-to-r from-[#056b4f] to-[#04523c] active:opacity-90 active:scale-[0.99]'
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="4" y="2" width="16" height="20" rx="2"></rect>
                  <line x1="8" y1="6" x2="16" y2="6"></line>
                  <line x1="16" y1="14" x2="16" y2="18"></line>
                  <path d="M16 10h.01"></path>
                  <path d="M12 10h.01"></path>
                  <path d="M8 10h.01"></path>
                  <path d="M12 14h.01"></path>
                  <path d="M8 14h.01"></path>
                  <path d="M12 18h.01"></path>
                  <path d="M8 18h.01"></path>
                </svg>
                <span>开始成绩计算</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
